import React, { createContext, useContext, useState, useId } from 'react';
import { cn } from '../../utils/cn';

const TabsCtx = createContext(null);

export function Tabs({ defaultValue, value, onChange, variant = 'underline', children, className }) {
  const id = useId();
  const [internal, setInternal] = useState(defaultValue);
  const active = value !== undefined ? value : internal;
  const set = (v) => { setInternal(v); onChange?.(v); };

  return (
    <TabsCtx.Provider value={{ active, set, variant, id }}>
      <div className={className}>{children}</div>
    </TabsCtx.Provider>
  );
}

function TabsList({ children, className }) {
  const { variant } = useContext(TabsCtx);
  const cls = {
    underline:  'flex gap-4 border-b border-border',
    pills:      'inline-flex p-0.5 bg-bg-2 rounded-md',
    segmented:  'inline-flex border border-border rounded-md overflow-hidden bg-surface',
  }[variant];
  return <div role="tablist" className={cn(cls, className)}>{children}</div>;
}

function Tab({ value, children, className }) {
  const { active, set, variant } = useContext(TabsCtx);
  const isActive = active === value;
  const cls = {
    underline:  cn(
      'px-1 py-2.5 text-sm font-medium transition-colors -mb-px border-b-2',
      isActive ? 'text-text border-ink' : 'text-mute border-transparent hover:text-text',
    ),
    pills: cn(
      'px-3 h-7 inline-flex items-center text-sm font-medium rounded transition-colors',
      isActive ? 'bg-surface text-text shadow-xs' : 'text-mute hover:text-text',
    ),
    segmented: cn(
      'px-3 h-9 inline-flex items-center text-sm font-medium transition-colors',
      isActive ? 'bg-ink text-cream' : 'text-mute hover:bg-bg-2',
      '[&:not(:last-child)]:border-r [&:not(:last-child)]:border-border',
    ),
  }[variant];

  return (
    <button type="button" role="tab" aria-selected={isActive} onClick={() => set(value)} className={cn(cls, className)}>
      {children}
    </button>
  );
}

function TabPanel({ value, children, className }) {
  const { active } = useContext(TabsCtx);
  if (active !== value) return null;
  return <div role="tabpanel" className={cn('pt-4', className)}>{children}</div>;
}

Tabs.List = TabsList;
Tabs.Tab = Tab;
Tabs.Panel = TabPanel;

export default Tabs;
